import {
  AnalyticsService,
  AuthService,
  Employee,
  EmployeeService,
  EventService,
  GeogroupService,
  HttpService,
  Partner,
  UserPlace,
  UserService,
  i18nCommons,
  setLocales,
} from '@geovelo-frontends/commons';
import {
  ReactNode,
  Ref,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router';

import useEmployees from '../../hooks/employees';
import usePartnerGeogroupQuery from '../../hooks/use-partner-geogroup-query';
import usePartnerSubgroupsQuery from '../../hooks/use-partner-subgroups-query';
import useSwitchCompanyMutation from '../../hooks/use-switch-company-mutation';
import LoadingPage from '../../pages/loading';
import { TPendingInvitation } from '../../pages/not-linked/invitation-card';
import { AppContext, offersMap } from '../context';
import { DEFAULT_META_DESCRIPTION, DEFAULT_META_IMAGE } from '../default-meta';
import { languages } from '../i18n';

import routes, { TPaths, defaultGuestPath, defaultPrivatePath } from './routes';

export type TRouterRef = { selectCompany: (employee: Employee, partner: Partner) => void };

function Router(
  {
    cancellablePromise,
    cancelPromises,
    userWorks,
  }: {
    cancelPromises: () => void;
    cancellablePromise: <T>(p: Promise<T>) => Promise<T>;
    userWorks: UserPlace[] | undefined;
  },
  ref: Ref<TRouterRef>,
): React.JSX.Element {
  const [initialized, setInitialized] = useState(false);
  const eventIdSetRef = useRef(false);
  const {
    history,
    user: { current: currentUser },
    actions: {
      setCurrentEvent,
      setCurrentPartner,
      setPartnerType,
      setPartnerPermissions,
      setCurrentUser,
      setUserIsRegister,
      setUserEmployee,
      setUserEmployees,
      setUserTeam,
      setUserSite,
      setUserHome,
      setUserWorks,
      setUserCommutingTrips,
      setUserTransportHabits,
      setUserCreationReasonDialogSeen,
    },
  } = useContext(AppContext);
  const { i18n } = useTranslation();
  const { pathname, state } = useLocation();
  // Cette page définit ses propres métadonnées : React 19 ne déduplique pas les balises meta.
  const isCO2SavingWebview = pathname === '/webviews/employee-co2-saving';
  const [searchParams, setSearchParams] = useSearchParams();
  const [isWebview, setWebview] = useState(pathname.indexOf('/webviews') === 0);
  const [isConnect, setConnect] = useState(pathname.indexOf('/connect') > 0);
  const [isOnboardingWebview, setOnboardingWebview] = useState(
    pathname.indexOf('/webviews/onboarding') === 0,
  );
  const [isAddCompanyWebview, setAddCompanyWebview] = useState(
    pathname.indexOf('/webviews/add-companny') === 0,
  );
  const navigate = useNavigate();
  const { mutate: switchCompany } = useSwitchCompanyMutation();
  const { selectEmployee } = useEmployees({
    isWebview,
    isOnboardingWebview,
    isAddCompanyWebview,
    cancellablePromise,
  });

  async function selectCompany(employee: Employee, partner: Partner) {
    switchCompany();
    sessionStorage.removeItem('event_id');
    setCurrentEvent(undefined);
    setUserEmployee(undefined);
    setUserTeam(undefined);
    setUserSite(undefined);
    setUserTransportHabits(undefined);
    setUserCreationReasonDialogSeen(undefined);
    setCurrentPartner(undefined);
    setPartnerType(undefined);
    setPartnerPermissions(offersMap.free.permissions);

    try {
      await selectEmployee(employee, partner);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }

  useImperativeHandle(ref, () => ({ selectCompany }));

  useEffect(() => {
    if (!searchParams.get('event_id') && (pathname === '/welcome/login' || pathname === '/try')) {
      sessionStorage.removeItem('event_id');
      setCurrentEvent(undefined);
      eventIdSetRef.current = false;
    }
  }, [pathname]);

  useEffect(() => {
    async function init() {
      const eventIdParam = searchParams.get('event_id');
      const eventId = eventIdParam ?? sessionStorage.getItem('event_id');
      if (eventId) {
        try {
          const event = await EventService.getEvent(Number(eventId));
          setCurrentEvent(event);
          sessionStorage.setItem('event_id', String(event.id));
          eventIdSetRef.current = true;
        } catch {
          setCurrentEvent(null);
          sessionStorage.removeItem('event_id');
        }
      } else if (!eventIdSetRef.current) {
        setCurrentEvent(null);
      }
      setInitialized(true);
    }
    init();
  }, [searchParams.get('event_id')]);

  useEffect(() => {
    if (initialized) {
      if (!isWebview) {
        const isWelcomePage = pathname.indexOf('/welcome') === 0;
        const isJoinPage = pathname.indexOf('/join') === 0;

        if (!isWelcomePage && !isJoinPage) {
          const userId = searchParams.get('user-id');
          const authorizationToken = searchParams.get('auth-token');

          searchParams.delete('user-id');
          searchParams.delete('auth-token');

          setSearchParams(searchParams);

          if (userId && authorizationToken) {
            try {
              HttpService.authorizationToken = `Token ${authorizationToken}`;
              localStorage.setItem('authorization_token', `Token ${authorizationToken}`);
              localStorage.setItem('user_id', `${userId}`);
            } catch {
              console.error('localStorage access is denied');
            }
          }

          initUser();
        } else {
          setCurrentUser(null);
        }
      }

      try {
        const _sessionId = searchParams.get('session-id');
        if (_sessionId) {
          const sessionId = parseInt(_sessionId);
          if (!Number.isNaN(sessionId)) {
            AnalyticsService.setSessionId(sessionId);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
  }, [initialized]);

  useEffect(() => {
    if (
      history.current &&
      (history.current.length === 0 ||
        history.current[history.current.length - 1].localeCompare(pathname) !== 0)
    ) {
      history.current.push(pathname);
    }

    setWebview(pathname.indexOf('/webviews') === 0);
    setConnect(pathname.indexOf('/connect') > 0);
    setOnboardingWebview(pathname.indexOf('/webviews/onboarding') === 0);
    setAddCompanyWebview(pathname.indexOf('/webviews/add-company') === 0);
  }, [pathname]);

  useEffect(() => {
    i18nCommons.changeLanguage(i18n.language);
    setLocales(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (currentUser) {
      const {
        parameters: { language: userLanguage },
      } = currentUser;
      if (i18n.language !== userLanguage && languages[userLanguage]) {
        i18n.changeLanguage(userLanguage);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (!initialized) return;

    if (currentUser) {
      getEmployeeAndPartner();
      getUserPlaces();
    } else if (currentUser === null) {
      setUserHome(null);
      setUserWorks([]);
      setUserCommutingTrips([]);
      setPartnerType(null);
      setPartnerPermissions(offersMap.free.permissions);
    }

    return () => {
      cancelPromises();
      setUserEmployee(undefined);
      setUserEmployees(undefined);
      setUserIsRegister(undefined);
      setUserTeam(undefined);
      setUserSite(undefined);
      setUserHome(undefined);
      setUserWorks(undefined);
      setUserTransportHabits(undefined);
      setUserCreationReasonDialogSeen(undefined);
      setCurrentPartner(undefined);
      setPartnerType(undefined);
      setPartnerPermissions(offersMap.free.permissions);
    };
  }, [initialized, currentUser]);

  async function signOut() {
    await AuthService.signOut();
    setCurrentUser(null);
  }

  async function initUser() {
    try {
      const user = await UserService.getCurrentUser();

      setCurrentUser(user);
      if (user) setUserIsRegister(false);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }

  async function getUserPlaces() {
    try {
      const places = await cancellablePromise(UserService.getPlaces());
      setUserHome(places.find(({ type }) => type === 'home') || null);
      setUserWorks(places.filter(({ type }) => type === 'work'));
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        setUserHome(null);
        setUserWorks([]);
      }
    }
  }

  async function getEmployeeAndPartner() {
    if (state?.isSubscription || isConnect || isAddCompanyWebview) return;

    const searchParam = searchParams.get('partner-id');
    const companyId = searchParam ? parseInt(searchParam) : null;

    searchParams.delete('partner-id');

    let companyCode: string | null = null;
    try {
      companyCode = localStorage.getItem('company_code') || null;
    } catch {
      console.error('localStorage access is denied');
    }

    try {
      let employees = await cancellablePromise(EmployeeService.getEmployees());
      let employee: Employee | null;
      let _partner: Partner | null;
      ({ employee, partner: _partner } =
        employees.find(
          ({ partner }) =>
            (companyId && !Number.isNaN(companyId) && partner.id === companyId) ||
            (companyCode && partner.code === companyCode),
        ) ||
        employees.find(({ employee }) => employee.status === 'joined') ||
        employees[0] ||
        {});

      if (!employee && isOnboardingWebview) {
        const userGeogroups = await GeogroupService.getUserGeogroups();
        const geogroup = userGeogroups.find(
          ({ status, group }) =>
            ['automaticallyJoin', 'manuallyJoin'].includes(status) && group.type === 'company',
        )?.group;
        if (geogroup?.partner) {
          const _userId = searchParams.get('user-id');
          const userId = parseInt(_userId || '');

          if (!Number.isNaN(userId)) {
            const { code: invitationCode } = await GeogroupService.getInvitationLink(geogroup.id);
            await EmployeeService.createEmployee(geogroup.partner, {
              userId,
              invitationCode,
            });
            employees = await cancellablePromise(EmployeeService.getEmployees());
            ({ employee, partner: _partner } = employees[0] || {});
          }
        }
      }

      if (
        (!employee || employee.status === 'waitingForApproval' || !employee?.partner) &&
        !isOnboardingWebview
      ) {
        if (!employee || employee.status === 'waitingForApproval') {
          setUserEmployee(null);
          setUserEmployees([]);

          if (employee?.partner) {
            navigate('/welcome/not-linked', {
              state: { allowNotLinkedEmployee: true, partner: employee.partner },
            });
          } else {
            const results = await cancellablePromise(EmployeeService.getEmployeesInvitations());

            navigate('/welcome/not-linked', {
              state: {
                allowNotLinkedEmployee: true,
                pendingInvitations: results.map<TPendingInvitation>(({ partner, employee }) => ({
                  partner: { id: partner.id, title: partner.title, icon: partner.icon },
                  employee: { id: employee.id },
                })),
              },
            });
          }
        } else {
          signOut();
        }

        return;
      }

      await selectEmployee(employee, _partner);

      setUserEmployees(employees.filter(({ employee }) => employee.status === 'joined'));
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        if (!isOnboardingWebview && !isAddCompanyWebview) signOut();
      }
    }
  }

  return (
    <>
      <>
        <meta content="website" property="og:type" />
        <meta content="%PUBLIC_URL%" property="og:url" />
        {!isCO2SavingWebview && (
          <>
            <meta content={DEFAULT_META_DESCRIPTION} name="description" />
            <meta content="Geovelo Entreprise" property="og:title" />
            <meta content={DEFAULT_META_DESCRIPTION} property="og:description" />
            <meta content={DEFAULT_META_IMAGE} property="og:image" />
          </>
        )}
      </>
      <Routes>
        {routes.map(({ isPrivate, isGuest, element, ...otherProps }) => {
          if (isPrivate)
            return (
              <Route
                element={
                  <PrivateRoute path={otherProps.path} userWorks={userWorks}>
                    {element}
                  </PrivateRoute>
                }
                key={otherProps.path || 'index'}
                {...otherProps}
              />
            );
          if (isGuest)
            return (
              <Route
                element={<GuestRoute>{element}</GuestRoute>}
                key={otherProps.path || 'index'}
                {...otherProps}
              />
            );

          return <Route element={element} key={otherProps.path || 'index'} {...otherProps} />;
        })}
        <Route element={<Navigate to="/" />} path="*" />
      </Routes>
    </>
  );
}

function GuestRoute({ children }: { children: ReactNode }): React.JSX.Element {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const {
    app: { currentEvent: contextCurrentEvent },
    partner: { type: partnerType },
    user: { current: currentUser, employee },
  } = useContext(AppContext);
  const eventId = contextCurrentEvent?.id ?? (Number(searchParams.get('event_id')) || undefined);
  if (currentUser && !state?.allowNotLinkedEmployee && !state?.isSubscription) {
    const isAdmin = employee?.role === 'administrator' || employee?.role === 'animator';
    const to =
      eventId && partnerType !== 'holding'
        ? isAdmin
          ? `/events/${eventId}?auto-subscription=true`
          : `/events/${eventId}`
        : state?.requestedPath || defaultPrivatePath;
    return <Navigate replace to={to} />;
  }

  return <>{children}</>;
}

function PrivateRouteContent({ children }: { children: ReactNode }): React.JSX.Element {
  return <>{children}</>;
}

function PrivateRoute({
  userWorks: _userWorks,
  path,
  children,
}: {
  children: ReactNode;
  path?: TPaths | '*';
  userWorks: UserPlace[] | undefined;
}): React.JSX.Element {
  const {
    user: { current: currentUser, employee, transportHabits, home: userHome, team },
    partner: { current: currentPartner, type: partnerType },
  } = useContext(AppContext);
  const { data: partnerSubgroups } = usePartnerSubgroupsQuery(currentPartner, {
    isHolding: partnerType === 'holding',
  });
  const teams = partnerSubgroups?.teams;
  const { data: currentPartnerGeogroup } = usePartnerGeogroupQuery(currentPartner);
  const { t } = useTranslation();
  const { pathname, search, state } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    window.document.getElementsByTagName('main')[0]?.scrollTo(0, 0);
  }, [pathname]);

  if (state?.isSubscription) return <PrivateRouteContent>{children}</PrivateRouteContent>;

  if (currentUser === null) {
    const eventIdMatch = pathname.match(/^\/events\/(\d+)/);
    const loginPath = eventIdMatch ? `/try?event_id=${eventIdMatch[1]}` : defaultGuestPath;
    return <Navigate state={{ requestedPath: `${pathname}${search}` }} to={loginPath} />;
  }

  if (employee === null)
    return state?.allowNotLinkedEmployee ? (
      <PrivateRouteContent>{children}</PrivateRouteContent>
    ) : (
      <></>
    );

  if (
    currentUser === undefined ||
    employee === undefined ||
    userHome === undefined ||
    _userWorks === undefined ||
    team === undefined ||
    teams === undefined ||
    transportHabits === undefined ||
    (currentUser && (currentPartner === undefined || currentPartnerGeogroup === undefined))
  )
    return <LoadingPage text={t('commons.user.loading')} />;

  if (partnerType !== 'holding' && pathname !== `/${path}`) {
    if (
      !employee.hasFirstName ||
      !employee.hasLastName ||
      !employee.hasProfessionalEmail ||
      !employee.eulaAccepted ||
      !employee.privacyPolicyAccepted
    ) {
      return <Navigate to="/onboarding/personal-data" />;
    }

    if (employee.isCollaborator && !employee.hasAtLeastOneCommutingTrip) {
      return <Navigate to="/onboarding/addresses" />;
    }

    if (
      employee.isCollaborator &&
      employee.departmentIsMissing &&
      Boolean(teams && teams.length > 0)
    ) {
      return <Navigate to="/onboarding/team" />;
    }

    if (employee.isCollaborator && !employee.hasTransportHabits) {
      return <Navigate to="/onboarding/transport-modes" />;
    }
  }

  return <PrivateRouteContent>{children}</PrivateRouteContent>;
}

export default forwardRef(Router);
